import {
  getAvatar,
  charAvatar,
  isInfluenceResource,
  getImage,
  mapApiPlatformToAdapted,
  translate,
} from 'utils';
import { instagram_comment_avatar } from 'ui-components/ui-images';
import { isString } from 'lib';
import translation from 'translation';
const { t } = translation;

export const contentAdapter = (content, _platform) => {
  const platform =
    _platform === 'nativePlatforms'
      ? mapApiPlatformToAdapted(content.platform)
      : _platform;

  const commonFields = {
    platform: _platform,
    id: content.id,
    is_seen: content?.is_seen,
    contentTags: content?.content_tags || undefined,
    dateTime: content?.time,
    url: content?.url || undefined,
    platformUserId: content?.['relation.id'],
    robotSeeDate: content?.robot_see_date,
    sentiment: content?.sentiment,
    sentimentNeutral: content?.sentiment_neutral,
    sentimentNegative: content?.sentiment_negative,
    sentimentPositive: content?.sentiment_positive,
    likeCount: content?.like_count,
    viewCount: content?.view_count,
    commentCount: content?.comment_count,
    mediaType: content?.media_type,
    categories: content?.categories || {},
    insertedAt: content?.document_inserted_at,
  };

  const socialCommonFields = {
    avatar: content?.avatar ? getAvatar(content?.avatar, platform) : null,
    charAvatar: !content?.avatar
      ? charAvatar(content?.title || content?.username)
      : null,
    name: content?.title || t(`common:${platform}NotDetermined`),
    username: content?.username,
    description: content?.description || '',
    smartHighlight: content?.smart_highlight || '',
    isVerified: isString(content?.is_verified)
      ? parseInt(content?.is_verified)
      : content.is_verified,
    influence: !!isInfluenceResource(content?.['relation.influence_category']),
    isVipResource: content?.resource_vip ?? false,
    extractedHashtag: content?.extracted_hashtag || null,
    extractedUrl: content?.extracted_url || null,
    extractedMention: content?.extracted_mention || null,
    grade: content?.grade || null,
  };

  let adaptedContent = {};

  if (platform === 'news') {
    adaptedContent = {
      ...commonFields,
      avatar: content?.['relation.avatar']
        ? getAvatar(content?.['relation.avatar'], 'news')
        : null,
      charAvatar: !content?.['relation.avatar']
        ? charAvatar(content?.['relation.title'])
        : null,
      name: content?.['relation.title'],
      username: content?.['relation.url'] || t('common:unknown'),
      title: content?.title,
      content: content?.content || '',
      description: content?.description || '',
      smartHighlight: content?.smart_highlight || '',
      republishersCount: content?.copy_count,
      republishers: content?.republishers || [],
      contentSource: content?.source,
      originalPostId: content?.source_id,
      influence: !!isInfluenceResource(
        content?.['relation.influence_category']
      ),
      image: content?.image,
      images: content?.images,
      galleries: content?.images,
      newsViewCount: content?.view_count,
      newsViewCountType: content?.view_count_type,
    };
  }

  if (platform === 'newspaper') {
    adaptedContent = {
      ...commonFields,
      avatar: content?.resource_avatar
        ? getAvatar(content?.resource_avatar, 'newspaper')
        : null,
      charAvatar: !content?.resource_avatar
        ? charAvatar(content?.resource_title || content?.resource_id)
        : null,
      name: content?.resource_title || t('common:newspaperNotDetermined'),
      username: content?.resource_id || t('common:unknown'),
      title: content?.title,
      downtitle: content?.downtitle || '',
      uptitle: content?.uptitle || '',
      description: content?.description,
      content: content?.content,
      platformUserId: content?.resource_id,
      pageNumber: content?.page_number,
      image: content?.image
        ? getImage({ data: content?.image, platform: 'newspaper' })
        : null,
      galleries: content?.image && [
        getImage({ data: content?.image, platform: 'newspaper' }),
      ],
    };
  }

  if (platform === 'kiosk') {
    adaptedContent = {
      ...commonFields,
      name: content?.title || t('common:newspaperNotDetermined'),
      username: content?.resource_id || t('common:unknown'),
      title: content?.title,
      platformUserId: content?.resource_id,
      pdf: content?.pdf,
      shareLink: content?.share_link,
      isBookmarked: content?.is_bookmarked ?? false,
      resourceNumber: content?.resource_number,
      resourceCategories: content?.resource_categories,
      image: content?.image
        ? getImage({ data: content?.image, platform: 'newspaper' })
        : null,
      galleries: content?.image && [
        getImage({ data: content?.image, platform: 'newspaper' }),
      ],
    };
  }

  if (
    platform === 'instagramComment' ||
    platform === 'instagramCommentByPost'
  ) {
    adaptedContent = {
      ...commonFields,
      description: content?.description,
      platformUserId: content?.['post_relation.id'],
      postId: content?.['post.id'],
      postText: content?.['post.text'],
      postImages: content?.['post.images'],
      postAvatar: content?.['post_relation.avatar'],
      postFullName: content?.['post_relation.full_name'],
      avatar: instagram_comment_avatar,
      name: t('common:instagramNotDetermined'),
      postGalleries: getImage({
        data: {
          slides: content?.['post.slides'],
          images: content?.['post.images'],
        },
        platform: 'instagram',
      }),
      postContent: content?.['post.text'],
      postLikes: content?.['post.like_count'],
      postComments: content?.['post.comment_count'],
    };
  }

  if (platform === 'instagram') {
    adaptedContent = {
      ...commonFields,
      ...socialCommonFields,
      likeCount: content?.like_count,
      commentCount: content?.comment_count,
      viewCount: content?.view_count,
      followerCount: content?.follower_count || 0,
      image: getImage({ data: content, platform: 'instagram' }),
      galleries: getImage({ data: content, platform: 'instagram' }),
      code: content?.code,
    };
  }

  if (platform === 'twitter') {
    adaptedContent = {
      ...commonFields,
      ...socialCommonFields,
      likeCount: content?.like_count,
      republishersCount: content?.copy_count,
      republishers: content?.republishers || [],
      media: content?.media,
      isReply: !!content?.in_reply_to_post_id,
      replyerUsername: content?.in_reply_to_username || null,
      replyToText: content?.in_reply_to_text || null,
      isQuote: !!(content?.is_quote && content?.is_quote !== '0'),
      replyCount: content?.reply_count,
      quote: content?.quoted_id
        ? {
            id: content?.['quoted_status.id'],
            avatar: content?.['quoted_status.avatar'],
            title: content?.['quoted_status.title'],
            username: content?.['quoted_status.username'],
            description: content?.['quoted_status.description'],
            url: content?.['quoted_status.url'],
          }
        : undefined,
      mention: content?.mention,
      retweetedId: content?.['retweeted_status.id'],
      retweetStatusId: content?.['retweeted_status.id'],
      retweetTextRange: content?.['retweeted_status.display_text_range'],
      textRange: content?.display_text_range,
      image: getImage({
        data: content,
        platform: 'twitter',
      })?.photos?.[0],
      galleries: getImage({
        data: content,
        platform: 'twitter',
      }),
      followerCount: content?.follower_count || 0,
    };
  }

  if (platform === 'virasty') {
    adaptedContent = {
      ...commonFields,
      ...socialCommonFields,
      isVerified: !!content?.resource_is_verified,
      reactions: content?.reactions,
      platformUserId: content?.resource_id,
      likeCount: content?.reactions_count,
      republishersCount: content?.repost_count,
      quoteCount: content?.quote_count,
      republishers: content?.republishers || [],
      media: content?.media,
      isReply: !!Number(content?.in_reply_to_post_id),
      replyerUsername: content?.in_reply_to_username || null,
      replyToText: content?.in_reply_to_text || null,
      isQuote: !!content?.is_quoted_post,
      replyCount: content?.reply_count,
      quote: content?.['quoted_status.id']
        ? {
            id: content?.['quoted_status.id'],
            title: content?.['quoted_status.title'],
            username: content?.['quoted_status.username'],
            description: content?.['quoted_status.description'],
            url: content?.['quoted_status.url'],
          }
        : undefined,
      mention: content?.['reposted_status.username'],
      retweetedId: content?.['reposted_status.id'],
      retweetStatusId: content?.['reposted_status.id'],
      image: getImage({
        data: content,
        platform: 'virasty',
      })?.photos?.[0],
      galleries: getImage({
        data: content,
        platform: 'virasty',
      }),
      followerCount: content?.resource_followers_count || 0,
    };
  }

  if (platform === 'telegram') {
    adaptedContent = {
      ...commonFields,
      ...socialCommonFields,
      republishersCount: content?.copy_count,
      republishers: content?.republishers || [],
      commentCount: content?.comment_count,
      viewCount: content?.view_count,
      messageType: content?.message_type,
      memberCount: content?.member_count || 0,
      image: getImage({ data: content, platform: 'telegram' }),
      galleries:
        content?.message_type === 'photo'
          ? [getImage({ data: content, platform: 'telegram' })]
          : null,
      mediaType: content.message_type,
    };
  }

  if (
    platform === 'eitaa' ||
    platform === 'rubika' ||
    platform === 'bale' ||
    platform === 'soroush'
  ) {
    adaptedContent = {
      ...commonFields,
      ...socialCommonFields,
      commentCount: content?.comment_count || 0,
      viewCount: content?.view_count || 0,
      memberCount: content?.member_count || 0,
      contentPlatform: content?.platform || '',
      republishersCount: content?.copy_count,
      messageType: content?.message_type,
    };
  }

  if (platform === 'instagramStories') {
    adaptedContent = {
      id: content?.id,
      platformUserId: content?.resource_id,
      avatar: content?.avatar ? getAvatar(content?.avatar, 'story') : null,
      name: content?.title || t('common:instagramNotDetermined'),
      username: content?.resource_username || null,
      dateTime: content?.time,
      image:
        getImage({
          data: content,
          platform: 'story',
          sendPlatform: 'story',
        }) || '',
      video: getImage({
        data: content,
        platform: 'story',
        sendPlatform: 'instagram_vip_video',
        isVideo: true,
      }),
      isVerified: content?.is_verified || false,
      isVipResource: content?.resource_vip || false,
      description: content?.raw_content || null,
      hashtags: content?.extracted_hashtag || [],
      mentions: content?.extracted_mention || [],
      urls: content?.extracted_url || [],
      charAvatar: !content?.avatar
        ? charAvatar(content?.resource_username)
        : null,
      topCategory: translate[content?.top_category] || null,
      platform: platform,
      mediaType: content?.media_type,
    };
  }

  if (platform === 'linkedin') {
    adaptedContent = {
      ...commonFields,
      ...socialCommonFields,
      avatar: getAvatar(content?.image, 'linkedin'),
      shareCount: content?.share_count,
    };
  }

  return adaptedContent;
};

export const summarizerAdapter = (data, meta) => {
  const appliedFilters = {
    langs: 'filter:language',
    content_tag_ids: 'filter:contentsTag',
    extracted_phrases: 'filter:wordCloud',
    extracted_hashtags: 'filter:hashtagCloud',
    sentiments: 'filter:contentsSentiment',
    //advert_noise: instagram
    min_advert_noise: 'filter:advertLevel',
    max_advert_noise: 'filter:advertLevel',
    //relation_influence: twitter, telegram, news, instagram
    resource_influence_categories: 'filter:contentBasedOn',
    //view: telegram
    min_view: 'filter:contentBasedOn',
    max_view: 'filter:contentBasedOn',
    //lik: twitter, instagram
    min_like: 'filter:contentBasedOn',
    max_like: 'filter:contentBasedOn',
    //copy: twitter
    min_copy: 'filter:contentBasedOn',
    max_copy: 'filter:contentBasedOn',
    //comment: instagram
    min_comment: 'filter:contentBasedOn',
    max_comment: 'filter:contentBasedOn',
    //source: twitter, telegram, news
    source: 'filter:contentType',
    //categories: twitter, telegram, news, instagram
    categories: 'filter:contentCategory',
    sub_categories: 'filter:contentCategory',
    //resource_categories: newspaper
    resource_categories: 0,
    //tag_id: news, twitter, telegram, newspaper, instagram, instagramComment
    tag_id: 'filter:resourceCategory',
    //resource_ids: instagramComment
    post_resource_ids: 'filter:resourceSearch',
    //age: twitter, instagram
    m3_age: 'filter:ageRange',
    gender: 'filter:gender',
    media_types: 'filter:mediaType',
  };

  const filters = [
    ...new Set(
      Object.entries(data.request_details)
        .filter(
          ([key, value]) => value && value.length !== 0 && appliedFilters[key]
        )
        .map(([key]) => appliedFilters[key])
    ),
  ];

  return { ...data, filters, meta };
};
